import React, { useState, useEffect } from 'react';
import {
  X,
  History,
  RotateCw,
  AlertCircle,
  CheckCircle2,
  Clock,
  Coins,
  Cpu,
  Eye,
  Layers,
  FileText,
  ExternalLink,
  Sparkles,
} from 'lucide-react';
import { getTenderAgentHistory, getTenderAgentRunDetail } from '../../../services/tenderAgentApi';

export default function TenderRunHistoryModal({ onClose }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRun, setSelectedRun] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const res = await getTenderAgentHistory({ limit: 30 });
      const runsList = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.runs)
        ? res.data.runs
        : Array.isArray(res?.runs)
        ? res.runs
        : [];
      setRuns(runsList);
    } catch (err) {
      console.error('Erreur chargement historique des runs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectRun = async (r) => {
    setSelectedRun(r);
    if (!r.items || r.items.length === 0) {
      try {
        setLoadingDetail(true);
        const detailRes = await getTenderAgentRunDetail(r.runId || r.run_id);
        if (detailRes?.success && detailRes.data) {
          setSelectedRun(detailRes.data);
        }
      } catch (err) {
        console.error('Erreur chargement détail run:', err);
      } finally {
        setLoadingDetail(false);
      }
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return isNaN(d.getTime())
      ? dateStr
      : d.toLocaleString('fr-FR', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'RUNNING':
        return <span className="ao-badge ao-badge-running">En cours</span>;
      case 'COMPLETED':
      case 'SUCCESS':
        return <span className="ao-badge ao-badge-success">Succès</span>;
      case 'FAILED':
      case 'ERROR':
        return <span className="ao-badge ao-badge-urgent">Échec</span>;
      default:
        return <span className="ao-badge ao-badge-neutral">{status || 'Inconnu'}</span>;
    }
  };

  return (
    <div className="ao-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="ao-modal-card ao-modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="ao-modal-header">
          <div className="flex items-center space-x-2.5">
            <div className="ao-modal-icon-badge">
              <History size={20} className="text-amber-500" />
            </div>
            <div>
              <h3 className="ao-modal-title">Historique des Exécutions du Tender Agent</h3>
              <p className="ao-modal-subtitle">Traçabilité complète des scans TUNEPS, tokens consommés et coûts estimés</p>
            </div>
          </div>
          <button type="button" className="ao-modal-close-btn" onClick={onClose} aria-label="Fermer la modale">
            <X size={20} />
          </button>
        </div>

        <div className="ao-modal-body">
          {loading ? (
            <div className="ao-loading-state">
              <RotateCw size={28} className="animate-spin text-amber-500 mb-2" />
              <p>Chargement de l'historique des exécutions...</p>
            </div>
          ) : runs.length === 0 ? (
            <div className="ao-empty-box">
              <History size={40} className="text-gray-300 mb-2" />
              <p className="font-semibold text-gray-700">Aucune exécution enregistrée pour le moment.</p>
              <p className="text-xs text-gray-400">Lancez une collecte manuelle ou activez le planificateur automatique.</p>
            </div>
          ) : (
            <div className="ao-table-wrapper">
              <table className="ao-table">
                <thead>
                  <tr>
                    <th>Date & Heure</th>
                    <th>Mode</th>
                    <th>Déclencheur</th>
                    <th>Statut</th>
                    <th>Marchés Traités</th>
                    <th>Durée</th>
                    <th>Coût USD</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => (
                    <tr key={r._id || r.runId || r.run_id}>
                      <td className="font-semibold text-xs text-gray-800">
                        {formatDate(r.startedAt || r.started_at || r.createdAt)}
                      </td>
                      <td>
                        <span className="ao-source-badge">{r.mode}</span>
                      </td>
                      <td className="text-xs text-gray-600">
                        {(r.triggeredBy || r.triggered_by) === 'AUTOMATIC' ? '⏰ Automatique' : '👤 Manuel Admin'}
                      </td>
                      <td>{getStatusBadge(r.status)}</td>
                      <td className="text-xs">
                        <span className="font-bold text-gray-800">{r.totalDiscovered ?? r.total_discovered ?? 0}</span> découverts
                        <br />
                        <span className="font-bold text-emerald-600">{r.totalIngested ?? r.total_ingested ?? 0}</span> ingérés
                      </td>
                      <td className="text-xs text-gray-600">
                        {(r.durationSeconds ?? r.duration_seconds) ? `${Number(r.durationSeconds ?? r.duration_seconds).toFixed(1)} s` : '—'}
                      </td>
                      <td className="text-xs font-bold text-gray-800">
                        ${Number(r.estimatedCostUsd ?? r.estimated_cost_usd ?? 0).toFixed(4)}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="ao-btn-inspect"
                          onClick={() => handleSelectRun(r)}
                          title="Voir les détails et marchés découverts"
                        >
                          <Eye size={14} className="mr-1" />
                          <span>Détails</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Tiroir d'inspection d'un run */}
          {selectedRun && (
            <div className="ao-run-drawer">
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center space-x-2">
                  <span className="font-bold text-sm text-gray-900">Détails de l'Exécution</span>
                  <code className="ao-code-pill">{selectedRun.runId || selectedRun.run_id}</code>
                  <span className="ao-source-badge ml-2">{selectedRun.mode}</span>
                </div>
                <button
                  type="button"
                  className="ao-btn ao-btn-ghost text-xs"
                  onClick={() => setSelectedRun(null)}
                >
                  Masquer
                </button>
              </div>

              <div className="grid grid-cols-4 gap-3 text-xs mb-3">
                <div className="ao-spec-item">
                  <span className="ao-spec-label">Tokens Consommés</span>
                  <span className="ao-spec-val font-bold">{(selectedRun.tokensConsumed ?? selectedRun.tokens_consumed ?? 0).toLocaleString()}</span>
                </div>
                <div className="ao-spec-item">
                  <span className="ao-spec-label">Filtrés Hors-Sujet</span>
                  <span className="ao-spec-val font-bold">{selectedRun.totalFiltered ?? selectedRun.total_filtered ?? 0}</span>
                </div>
                <div className="ao-spec-item">
                  <span className="ao-spec-label">Analysés par LLM</span>
                  <span className="ao-spec-val font-bold">{selectedRun.totalAnalyzedLlm ?? selectedRun.total_analyzed_llm ?? 0}</span>
                </div>
                <div className="ao-spec-item">
                  <span className="ao-spec-label">Mises à Jour (Reports)</span>
                  <span className="ao-spec-val font-bold">{selectedRun.totalUpdated ?? selectedRun.total_updated ?? 0}</span>
                </div>
              </div>

              {selectedRun.errors?.length > 0 && (
                <div className="ao-error-callout mb-3">
                  <div className="font-semibold mb-1 flex items-center">
                    <AlertCircle size={14} className="mr-1.5" /> Erreurs signalées durant la collecte :
                  </div>
                  <ul className="list-disc list-inside">
                    {selectedRun.errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* ─── Liste Exhaustive des Marchés Découverts & Traités ─── */}
              <div className="mt-4 pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between mb-2.5">
                  <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center">
                    <FileText size={14} className="mr-1.5 text-amber-500" />
                    Marchés & Avis Découverts sur TUNEPS ({selectedRun.items?.length || 0})
                  </h4>
                  <span className="text-[11px] text-gray-500">
                    Cycle : <strong>{selectedRun.mode}</strong> ({formatDate(selectedRun.startedAt || selectedRun.started_at)})
                  </span>
                </div>

                {loadingDetail ? (
                  <div className="py-6 text-center text-xs text-gray-500">
                    <RotateCw size={18} className="animate-spin text-amber-500 mx-auto mb-2" />
                    Chargement des marchés découverts...
                  </div>
                ) : (!selectedRun.items || selectedRun.items.length === 0) ? (
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-500 text-center">
                    Aucun détail unitaire de marché persisté pour ce cycle.
                  </div>
                ) : (
                  <div className="ao-table-wrapper" style={{ maxHeight: '340px', overflowY: 'auto' }}>
                    <table className="ao-table ao-table-sm text-xs">
                      <thead>
                        <tr>
                          <th style={{ width: '130px' }}>N° TUNEPS</th>
                          <th>Objet de la Consultation / Marché</th>
                          <th style={{ width: '180px' }}>Acheteur Public</th>
                          <th style={{ width: '80px' }}>Type</th>
                          <th style={{ width: '80px', textAlign: 'center' }}>Score IA</th>
                          <th style={{ width: '130px' }}>Statut Cycle</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedRun.items.map((it, idx) => (
                          <tr key={idx} className="hover:bg-amber-50/40 transition-colors">
                            <td className="font-mono font-bold text-gray-800">
                              <a
                                href={`https://www.tuneps.tn`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-amber-600 hover:text-amber-700 hover:underline flex items-center"
                                title="Ouvrir le portail officiel TUNEPS"
                              >
                                {it.bid_no || it.bidNo}
                                <ExternalLink size={11} className="ml-1 opacity-70" />
                              </a>
                            </td>
                            <td>
                              <div className="font-semibold text-gray-800 line-clamp-2">{it.title}</div>
                              {it.message && (
                                <div className="text-[11px] text-gray-500 mt-0.5 line-clamp-1 italic">
                                  {it.message}
                                </div>
                              )}
                            </td>
                            <td className="text-gray-700 font-medium">
                              {it.buyer || 'Non spécifié'}
                            </td>
                            <td>
                              <span className="ao-source-badge">{it.tender_type || it.tenderType || 'AO'}</span>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <span className={`ao-score-pill ${(it.score || 0) >= 70 ? 'high' : (it.score || 0) >= 40 ? 'medium' : 'low'}`}>
                                {it.score || 0}/100
                              </span>
                            </td>
                            <td>
                              <span className={`ao-badge ${
                                it.status === 'INGESTED' ? 'ao-badge-success' :
                                it.status === 'DRY_RUN_EVALUATED' ? 'ao-badge-neutral' :
                                it.status === 'SKIPPED_NOT_RELEVANT' ? 'ao-badge-closed' : 'ao-badge-neutral'
                              }`}>
                                {it.status === 'INGESTED' ? 'Ingéré' :
                                 it.status === 'DRY_RUN_EVALUATED' ? 'Évalué (DRY)' :
                                 it.status === 'SKIPPED_NOT_RELEVANT' ? 'Écarté (Hors-sujet)' : it.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
